import React from 'react';
import { set, get } from 'lodash';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { checkCurrency, checkIsNumber, roundOff, SelectBoxStyles, roundingOff, shortenStringWithTooltip } from '../../../../../../Components/helper';
import useDetailedCostingHooks from '../../Hooks/useDetailedCostingHooks';
import useShowHideSectionHooks from '../../Hooks/useShowHideSectionHooks';
import useValidationHooks from '../../Hooks/useValidationHooks';
import { detailedCostingFieldpath, errorpath } from '../../../../ProductCosting/fieldPath';
import { NewCheckbox } from '../../../../../../Components/PIM/common/NewCheckbox';

export function DetailedCostingExpandView(props) {
  const { expandData, index, chIndex, expandDetailedCostingChild, isExpanded, colourway, quantityUnitOfMeasure, sizeRange, lengthRange, productInfoObj } =
    props;
  const { productCostingstate, setProductCostingState, globalError } = productInfoObj;
  const { t } = useTranslation('common');
  const {
    productInfo: { currency },
    isGlobalUrl,
    uomOptionsArr,
    isBreakdown,
  } = productCostingstate;

  const { calculateDetailCosting } = useDetailedCostingHooks(productInfoObj);
  const { detailedCosting, bomDataError } = globalError;

  const { statusState, controlAmberColour, hideLengthCol, isDisabled, markAsCompleteState, hideColourCol } = useShowHideSectionHooks(productInfoObj);
  const { onChangeCheckIfErrorExist } = useValidationHooks();
  const {
    ProductCostingData: { productCostingPreviousData },
    UserDetails: { isSupplierLoggedIn },
    BOMConfigDetails: { isProductArchived },
  } = useSelector((state) => state);

  const handleInputChange = async (value, path, supplierPath) => {
    const copyState = productCostingstate;
    set(copyState, path, value);
    if (supplierPath) {
      if (isSupplierLoggedIn || isGlobalUrl) {
        const oldData = productCostingPreviousData;
        const previousValue = get(oldData, path);
        if (previousValue !== value) {
          set(copyState, supplierPath, true);
        } else set(copyState, supplierPath, false);
      } else set(copyState, supplierPath, false);
    }

    setProductCostingState((state) => ({ ...state, ...copyState }));
    await calculateDetailCosting(copyState);
  };

  const handleUomChange = async (value, bomPath, detailedCostingPath) => {
    const copyState = productCostingstate;
    set(copyState, bomPath, value);
    let detailedCostingData = get(copyState, detailedCostingPath, []);
    detailedCostingData = detailedCostingData?.map((keys) => {
      keys.expandData = keys?.expandData?.map((expKeys) => {
        expKeys.quantityUom = value;
        return expKeys;
      });
      return keys;
    });
    set(copyState, detailedCostingPath, detailedCostingData);
    setProductCostingState((state) => ({ ...state, ...copyState }));
    await calculateDetailCosting(copyState);
  };

  const updateError = (path) => onChangeCheckIfErrorExist(path, globalError);

  const truthyValues = () => {
    return isDisabled || statusState ? (
      quantityUnitOfMeasure?.name || '--'
    ) : (
      <>
        <Select
          options={uomOptionsArr}
          onChange={(selected) => {
            handleUomChange(selected, detailedCostingFieldpath(index).BOM_QUANTITY_UOM, detailedCostingFieldpath(index).BOM_DETAILED_COSTING_DATA);
            if (get(bomDataError, `[${index}].quantityUnitOfMeasure`, '')) {
              updateError(errorpath(index).DETAILED.ERROR_UOM);
            }
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          value={quantityUnitOfMeasure}
          isDisabled={statusState}
          styles={SelectBoxStyles}
          menuPosition="fixed"
          menuShouldBlockScroll
          menuShouldScrollIntoView
        />
        <span className="error-message">{get(bomDataError, `[${index}].quantityUnitOfMeasure`, '')}</span>
      </>
    );
  };

  return (
    <>
      {expandData.map((data, exIndex) => {
        return (
          <tr key={exIndex}>
            {exIndex === 0 && expandData.length !== 1 ? (
              <td>
                {isExpanded ? (
                  <button
                    className="btn-up-down"
                    type="button"
                    aria-label={t('COMMON_LABEL.OPEN')}
                    onClick={() => {
                      expandDetailedCostingChild(index, chIndex);
                    }}
                  >
                    <span className="icon-new-down-arrow-icon text-grey f-24 icon-inverse" />
                  </button>
                ) : (
                  <button
                    className="btn-up-down"
                    type="button"
                    aria-label={t('COMMON_LABEL.CLOSE')}
                    onClick={() => {
                      expandDetailedCostingChild(index, chIndex);
                    }}
                  >
                    <span className="icon-new-down-arrow-icon text-grey f-24 " />
                  </button>
                )}
              </td>
            ) : (
              <td />
            )}
            {!hideColourCol && <td>{colourway?.name}</td>}
            {!hideColourCol && <td>{colourway?.newReferences || '--'}</td>}
            <td>{isBreakdown ? Array.isArray(sizeRange) && shortenStringWithTooltip(sizeRange?.map((e) => e?.name).join(' - '), 15) : data?.size || ''}</td>
            {hideLengthCol && (
              <td>
                {isBreakdown
                  ? Array.isArray(lengthRange) && shortenStringWithTooltip(lengthRange?.map((e) => e?.name).join(' - '), 15)
                  : data?.length?.name || ''}
              </td>
            )}
            <td>
              <div className="d-flex align-items-center">
                <div className="flex-fill">
                  {statusState || isProductArchived ? (
                    roundOff(data?.quantityPerSizCol, 4)
                  ) : (
                    <input
                      onChange={(e) => {
                        if (checkIsNumber(e.target.value)) {
                          handleInputChange(
                            e.target.value,
                            detailedCostingFieldpath(index, chIndex, exIndex).EXPAND_QUANTITY_PER_SIZ_COL,
                            detailedCostingFieldpath(index, chIndex, exIndex).IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl,
                          );
                        }
                        if (get(detailedCosting, `[${index}][${chIndex}][${exIndex}].quantityPerSizCol`, '')) {
                          updateError(errorpath(index, chIndex, exIndex).DETAILED.ERROR_QUANTITY);
                        }
                      }}
                      value={data?.quantityPerSizCol}
                      type="text"
                      className={
                        data?.isChangedBySupplierTotalQuantityPerSizCol && controlAmberColour
                          ? 'suggested-data text-left form-control text-bordered'
                          : 'text-left form-control text-bordered'
                      }
                      readOnly={statusState}
                    />
                  )}
                </div>{' '}
              </div>
              <span className="error-message">{get(detailedCosting, `[${index}][${chIndex}][${exIndex}].quantityPerSizCol`, '')}</span>
            </td>
            <td>{chIndex === 0 && exIndex === 0 ? truthyValues() : quantityUnitOfMeasure?.name}</td>
            <td>
              <div className="d-flex align-items-center">
                <div className="flex-fill">
                  {statusState || isProductArchived ? (
                    roundOff(data?.pricePerUnit, 4)
                  ) : (
                    <input
                      onChange={(e) => {
                        if (checkIsNumber(e.target.value)) {
                          handleInputChange(
                            e.target.value,
                            detailedCostingFieldpath(index, chIndex, exIndex).EXPAND_PRICE_PER_UNIT,
                            detailedCostingFieldpath(index, chIndex, exIndex).IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT,
                          );
                        }
                        if (get(detailedCosting, `[${index}][${chIndex}][${exIndex}].pricePerUnit`, '')) {
                          updateError(errorpath(index, chIndex, exIndex).DETAILED.ERROR_PRICE_PER_UNIT);
                        }
                      }}
                      className={
                        data?.isChangedBySupplierPricePerUnit && controlAmberColour
                          ? 'suggested-data form-control text-bordered text-left'
                          : 'form-control text-bordered text-left'
                      }
                      value={data?.pricePerUnit}
                      readOnly={statusState}
                    />
                  )}
                </div>{' '}
              </div>
              <span className="error-message">{get(detailedCosting, `[${index}][${chIndex}][${exIndex}].pricePerUnit`, '')}</span>
            </td>
            <td className="text-left">
              {checkCurrency(currency, data?.totalPrice)}
              {roundingOff(data?.totalPrice, 4, true)}
            </td>
            <td>
              <div className="d-flex align-items-center">
                <div className="common-checkbox">
                  <NewCheckbox
                    size="sm"
                    onChange={(e) => {
                      handleInputChange(e.target.checked, detailedCostingFieldpath(index, chIndex, exIndex).IS_EXCLUDED);
                    }}
                    className="form-control text-bordered text-left"
                    checked={data?.isExcluded}
                    readOnly={statusState}
                    id={`exclude_${index}${chIndex}${exIndex}`}
                    disabled={markAsCompleteState || isProductArchived}
                  />
                </div>{' '}
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default DetailedCostingExpandView;
